import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, Download, ArrowRight, Plus, FileSearch, Pencil, Copy, ListChecks, Compass, Clock, ShieldCheck } from "lucide-react";
import { ChecklistDialog } from "@/components/checklist/ChecklistDialog";

export default function DashboardPage() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checklistFormId, setChecklistFormId] = useState(null);
  const navigate = useNavigate();

  const license = user?.license || { license_type: "particular", status: "active", can_generate_pdf: false };
  const isParticular = license.license_type === "particular";
  const isComercial = license.license_type === "comercial";
  const isAdmin = license.license_type === "admin";

  const fetchForms = async () => {
    try {
      const res = await api.get("/forms");
      setForms(res.data.forms || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load forms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/forms/${id}`);
      setForms((prev) => prev.filter((f) => f.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await api.post(`/forms/${id}/duplicate`);
      const newForm = res.data.form;
      setForms((prev) => [newForm, ...prev]);
      toast.success(lang === "pt" ? "Formulário duplicado" : lang === "es" ? "Formulario duplicado" : "Form duplicated");
      navigate(`/wizard/${newForm.id}`);
    } catch (e) {
      toast.error("Failed to duplicate");
    }
  };

  const handleDownload = async (id, code) => {
    try {
      const res = await api.get(`/forms/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `USCIS_${code}_${id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Try to parse error blob for license issues
      if (e.response?.status === 403 && e.response?.data) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          const detail = parsed.detail;
          if (detail?.code === "LICENSE_REQUIRED") {
            if (detail.license_type === "particular") {
              toast.info(lang === "pt"
                ? "Seu PDF está sendo processado pelo profissional. Você será notificado quando estiver pronto."
                : lang === "es"
                ? "Su PDF está siendo procesado por el profesional. Recibirá una notificación cuando esté listo."
                : "Your PDF is being processed by the professional. You'll be notified when ready.");
            } else {
              toast.error(lang === "pt"
                ? "Sua licença comercial expirou. Renove para gerar PDFs."
                : lang === "es"
                ? "Su licencia comercial expiró. Renueve para generar PDFs."
                : "Your commercial license expired. Renew to generate PDFs.");
              navigate("/pricing");
            }
            return;
          }
        } catch (_) { /* ignore parse */ }
      }
      toast.error("Failed to download PDF");
    }
  };

  return (
    <div data-testid="dashboard-page" className="mx-auto max-w-6xl px-6 py-12">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
            {lang === "pt" ? "Painel" : lang === "es" ? "Panel" : "Dashboard"}
          </div>
          <h1 className="mt-2 text-4xl font-black tracking-tight sm:text-5xl">
            {t.dashboard.title}
          </h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            {t.dashboard.description}
          </p>
        </div>
        <Button
          onClick={() => navigate("/wizard")}
          data-testid="dashboard-new-form-cta"
          className="hidden rounded-md bg-[hsl(var(--gold))] font-semibold text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-soft))] sm:inline-flex"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t.dashboard.create}
        </Button>
      </div>

      {/* Advisor callout */}
      <Link
        to="/advisor"
        data-testid="dashboard-advisor-link"
        className="mb-6 flex items-center justify-between gap-4 rounded-lg border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/[0.08] px-5 py-4 transition-colors hover:bg-[hsl(var(--gold))]/[0.14]"
      >
        <div className="flex items-center gap-3">
          <Compass className="h-5 w-5 text-[hsl(var(--gold))]" />
          <div>
            <div className="font-display text-base font-semibold text-foreground">
              {lang === "pt" ? "Não sabe qual formulário preencher?" : lang === "es" ? "¿No sabe qué formulario completar?" : "Not sure which form to fill?"}
            </div>
            <p className="text-xs text-muted-foreground">
              {lang === "pt" ? "Responda algumas perguntas e veja quais formulários combinam com seu caso (não é aconselhamento jurídico)." : lang === "es" ? "Responda algunas preguntas y vea qué formularios coinciden con su caso (no es asesoría legal)." : "Answer a few questions and see which forms match your case (not legal advice)."}
            </p>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-[hsl(var(--gold))]" />
      </Link>

      {/* License status banner */}
      {(isParticular || isComercial) && (
        <div
          data-testid="dashboard-license-banner"
          className={`mb-6 flex items-center justify-between gap-4 rounded-lg border px-5 py-4 ${
            isParticular
              ? "border-[hsl(var(--sage))]/40 bg-[hsl(var(--sage))]/[0.06]"
              : license.status === "active"
              ? "border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/[0.06]"
              : "border-destructive/40 bg-destructive/[0.06]"
          }`}
        >
          <div className="flex items-center gap-3">
            <ShieldCheck className={`h-5 w-5 ${isParticular ? "text-[hsl(var(--sage))]" : license.status === "active" ? "text-[hsl(var(--gold))]" : "text-destructive"}`} />
            <div>
              <div className="font-display text-sm font-semibold text-foreground">
                {isParticular
                  ? (lang === "pt" ? "Conta de cliente final" : lang === "es" ? "Cuenta de cliente final" : "End client account")
                  : license.status === "active"
                  ? (lang === "pt" ? "Licença comercial ativa" : lang === "es" ? "Licencia comercial activa" : "Commercial license active")
                  : license.status === "pending"
                  ? (lang === "pt" ? "Licença comercial pendente" : lang === "es" ? "Licencia comercial pendiente" : "Commercial license pending")
                  : (lang === "pt" ? "Licença comercial expirada" : lang === "es" ? "Licencia comercial expirada" : "Commercial license expired")}
              </div>
              <p className="text-xs text-muted-foreground">
                {isParticular
                  ? (lang === "pt" ? "Você preenche os dados. O profissional revisa e libera o PDF." : lang === "es" ? "Usted llena los datos. El profesional revisa y libera el PDF." : "You fill in the data. The professional reviews and releases the PDF.")
                  : license.status === "active" && license.expires_at
                  ? (lang === "pt" ? `Válida até ${new Date(license.expires_at).toLocaleDateString("pt-BR")} — PDFs ilimitados` : lang === "es" ? `Válida hasta ${new Date(license.expires_at).toLocaleDateString("es-ES")} — PDFs ilimitados` : `Valid until ${new Date(license.expires_at).toLocaleDateString("en-US")} — Unlimited PDFs`)
                  : (lang === "pt" ? "Ative sua licença para gerar PDFs ilimitados" : lang === "es" ? "Active su licencia para generar PDFs ilimitados" : "Activate your license to generate unlimited PDFs")}
              </p>
            </div>
          </div>
          {isComercial && license.status !== "active" && (
            <Link to="/pricing" data-testid="dashboard-activate-license">
              <Button size="sm" className="rounded-md bg-[hsl(var(--gold))] font-semibold text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-soft))]">
                {lang === "pt" ? "Ativar licença" : lang === "es" ? "Activar licencia" : "Activate license"}
              </Button>
            </Link>
          )}
        </div>
      )}

      {/* Materials downloads — visible to commercial + admin users */}
      {(isComercial || isAdmin) && (
        <div
          data-testid="dashboard-downloads-panel"
          className="mb-6 rounded-lg border border-border bg-card p-5"
        >
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--gold))]">
                {lang === "pt" ? "Materiais Comerciais" : lang === "es" ? "Materiales Comerciales" : "Sales Materials"}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {lang === "pt" ? "Baixe e envie aos seus clientes." : lang === "es" ? "Descárguelos y envíelos a sus clientes." : "Download and send to your clients."}
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-5">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/apresentacao`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-deck-html"
              className="group flex items-start gap-3 rounded-md border border-[hsl(var(--gold))]/60 bg-[hsl(var(--gold))]/[0.08] px-4 py-3 transition-colors hover:border-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/[0.14]"
            >
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--gold))]" />
              <div>
                <div className="font-display text-sm font-semibold text-foreground">
                  {lang === "pt" ? "Ver Apresentação" : lang === "es" ? "Ver Presentación" : "View Presentation"}
                </div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {lang === "pt" ? "Abre no navegador" : lang === "es" ? "Abre en navegador" : "Opens in browser"}
                </div>
              </div>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/apresentacao.pdf`}
              download
              data-testid="download-deck-pdf"
              className="group flex items-start gap-3 rounded-md border border-border bg-background px-4 py-3 transition-colors hover:border-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/[0.06]"
            >
              <Download className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--gold))]" />
              <div>
                <div className="font-display text-sm font-semibold text-foreground">
                  {lang === "pt" ? "Apresentação PDF" : lang === "es" ? "Presentación PDF" : "PDF Deck"}
                </div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  PDF · 10 páginas
                </div>
              </div>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/deck-pptx`}
              download
              data-testid="download-deck-pptx"
              className="group flex items-start gap-3 rounded-md border border-border bg-background px-4 py-3 transition-colors hover:border-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/[0.06]"
            >
              <Download className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--gold))]" />
              <div>
                <div className="font-display text-sm font-semibold text-foreground">
                  {lang === "pt" ? "Apresentação PPTX" : lang === "es" ? "Presentación PPTX" : "PPTX Deck"}
                </div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  PPTX · 10 slides
                </div>
              </div>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/kit-apresentacao`}
              download
              data-testid="download-kit-apresentacao"
              className="group flex items-start gap-3 rounded-md border border-border bg-background px-4 py-3 transition-colors hover:border-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/[0.06]"
            >
              <Download className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--gold))]" />
              <div>
                <div className="font-display text-sm font-semibold text-foreground">
                  {lang === "pt" ? "Kit de Apresentação" : lang === "es" ? "Kit de Presentación" : "Presentation Kit"}
                </div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  ZIP · 14 documentos
                </div>
              </div>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/kit-imigracao`}
              download
              data-testid="download-kit-imigracao"
              className="group flex items-start gap-3 rounded-md border border-border bg-background px-4 py-3 transition-colors hover:border-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/[0.06]"
            >
              <Download className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--gold))]" />
              <div>
                <div className="font-display text-sm font-semibold text-foreground">
                  {lang === "pt" ? "Kit de Imigração" : lang === "es" ? "Kit de Inmigración" : "Immigration Kit"}
                </div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  ZIP · checklist + guias
                </div>
              </div>
            </a>
          </div>
        </div>
      )}

      {loading ? (
        <div className="hairline p-10 text-center text-sm text-muted-foreground">
          {t.common.loading}
        </div>
      ) : forms.length === 0 ? (
        <div
          data-testid="dashboard-empty-state"
          className="hairline flex flex-col items-center gap-4 bg-card p-16 text-center"
        >
          <div className="flex h-14 w-14 items-center justify-center bg-secondary">
            <FileSearch className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="max-w-md text-sm text-muted-foreground">
            {t.dashboard.empty}
          </p>
          <Link to="/wizard">
            <Button
              data-testid="dashboard-empty-cta"
              className="rounded-none bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
            >
              {t.dashboard.create}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      ) : (
        <div className="hairline overflow-hidden bg-card">
          <div className="grid grid-cols-12 gap-4 border-b border-border bg-secondary/40 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <div className="col-span-4">{t.dashboard.column_form}</div>
            <div className="col-span-2">{t.dashboard.column_status}</div>
            <div className="col-span-3">{t.dashboard.column_updated}</div>
            <div className="col-span-3 text-right">
              {t.dashboard.column_actions}
            </div>
          </div>
          <ul className="divide-y divide-border">
            {forms.map((f) => (
              <li
                key={f.id}
                data-testid={`dashboard-form-row-${f.id}`}
                className="grid grid-cols-12 items-center gap-4 px-5 py-4"
              >
                <div className="col-span-4">
                  <div className="text-xl font-black tracking-tight">
                    {f.form_code}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {f.form_type}
                    </div>
                    {f.package_id && (
                      <span
                        data-testid={`dashboard-package-badge-${f.id}`}
                        className="border border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[0.06] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--primary))]"
                        title={f.package_id}
                      >
                        {f.package_id.replace("AOS_COMBO_", "").replace("_", " ")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <Badge
                    variant={f.status === "completed" ? "default" : "secondary"}
                    className={`rounded-none font-mono text-[10px] uppercase tracking-widest ${
                      f.status === "completed"
                        ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90"
                        : ""
                    }`}
                  >
                    {f.status === "completed" ? t.common.completed : t.common.draft}
                  </Badge>
                </div>
                <div className="col-span-3 font-mono text-xs text-muted-foreground">
                  {new Date(f.updated_at).toLocaleString(
                    lang === "es" ? "es-ES" : lang === "en" ? "en-US" : "pt-BR",
                  )}
                </div>
                <div className="col-span-3 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`dashboard-checklist-${f.id}`}
                    onClick={() => setChecklistFormId(f.id)}
                    className="rounded-none"
                    title={t.checklist.title}
                  >
                    <ListChecks className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`dashboard-edit-${f.id}`}
                    onClick={() => navigate(`/wizard/${f.id}`)}
                    className="rounded-none"
                    title={t.common.edit}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`dashboard-duplicate-${f.id}`}
                    onClick={() => handleDuplicate(f.id)}
                    className="rounded-none"
                    title={lang === "pt" ? "Duplicar" : "Duplicar"}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`dashboard-download-${f.id}`}
                    onClick={() => handleDownload(f.id, f.form_code)}
                    className="rounded-none"
                    title={
                      isParticular && !f.admin_approved
                        ? (lang === "pt" ? "Aguardando revisão do profissional" : lang === "es" ? "Esperando revisión del profesional" : "Awaiting professional review")
                        : t.common.download
                    }
                    disabled={isParticular && !f.admin_approved}
                  >
                    {isParticular && !f.admin_approved ? (
                      <Clock className="h-3.5 w-3.5" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`dashboard-delete-${f.id}`}
                    onClick={() => handleDelete(f.id)}
                    className="rounded-none hover:border-destructive hover:text-destructive"
                    title={t.common.delete}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <ChecklistDialog
        open={!!checklistFormId}
        onOpenChange={(v) => !v && setChecklistFormId(null)}
        formId={checklistFormId}
      />
    </div>
  );
}
